import { useState, useMemo, useEffect } from "react";
import { useAuth } from "wasp/client/auth";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { getServiceDefinitions, getServiceTypes, getServiceDefinitionById, updateServiceDefinition, deleteServiceDefinition } from "wasp/client/operations";
import { Filter, Plus, Upload } from "lucide-react";
import LoadingSpinner from "./layout/LoadingSpinner";
import { EnhancedActionButton } from "../components/ui/enhanced-page-layout";
import { TableFilterLayout } from "../components/ui/table-filter-layout";
import { AdminFlyoutPane } from "./components/AdminFlyoutPane";
import { FlyoutGridLayout, FlyoutGridSection, FlyoutGridRow } from "./components/FlyoutGridLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "../hooks/use-toast";
import { BulkUploadDialog } from "./bulk-upload/components/BulkUploadDialog";
import { serviceDefinitionConfig } from "./bulk-upload/configs";
import { useBulkUploadEnabled } from "./hooks/useBulkUploadEnabled";

export default function ServiceDefinitionsPage() {
  const { data: user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return <div>Loading...</div>;
  }

  const { data: serviceTypes = [] } = useQuery(getServiceTypes);
  const { data: serviceDefinitions = [], isLoading, error, refetch } = useQuery(getServiceDefinitions, {});
  const { isEnabled: isBulkUploadEnabled } = useBulkUploadEnabled();

  const [searchTerm, setSearchTerm] = useState("");
  const [slugFilter, setSlugFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedDefinitionId, setSelectedDefinitionId] = useState<string | null>(null);
  const [flyoutMode, setFlyoutMode] = useState<'view' | 'edit'>('view');

  // Editable fields
  const [editFormData, setEditFormData] = useState({
    name: '',
    slug: '',
    serviceTypeId: '',
    description: '',
    detailedDescription: '',
    typicalDuration: '',
    isRecurring: false,
    requiresBooking: false,
    isActive: true
  });

  // Fetch selected service definition details for flyout
  const { data: selectedDefinition, isLoading: loadingDefinition } = useQuery(
    getServiceDefinitionById,
    { id: selectedDefinitionId || '' },
    { enabled: !!selectedDefinitionId, keepPreviousData: true }
  );

  const filteredData = useMemo(() => {
    return (serviceDefinitions as any[]).filter((def: any) => {
      if (searchTerm && !def.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (slugFilter && !def.slug.toLowerCase().includes(slugFilter.toLowerCase())) {
        return false;
      }
      if (selectedTypeId && def.serviceTypeId !== selectedTypeId) {
        return false;
      }
      return true;
    });
  }, [serviceDefinitions, searchTerm, slugFilter, selectedTypeId]);

  // Initialize edit fields when service definition is selected
  useEffect(() => {
    if (selectedDefinition) {
      const def = selectedDefinition as any;
      setEditFormData({
        name: def.name || '',
        slug: def.slug || '',
        serviceTypeId: def.serviceTypeId || '',
        description: def.description || '',
        detailedDescription: def.detailedDescription || '',
        typicalDuration: def.typicalDuration?.toString() || '',
        isRecurring: def.isRecurring ?? false,
        requiresBooking: def.requiresBooking ?? false,
        isActive: def.isActive ?? true
      });
    }
  }, [selectedDefinition]);

  // Check if any changes have been made
  const definitionData = selectedDefinition as any;
  const hasChanges = definitionData ? (
    editFormData.name !== (definitionData.name || '') ||
    editFormData.slug !== (definitionData.slug || '') ||
    editFormData.serviceTypeId !== (definitionData.serviceTypeId || '') ||
    editFormData.description !== (definitionData.description || '') ||
    editFormData.detailedDescription !== (definitionData.detailedDescription || '') ||
    editFormData.typicalDuration !== (definitionData.typicalDuration?.toString() || '') ||
    editFormData.isRecurring !== (definitionData.isRecurring ?? false) ||
    editFormData.requiresBooking !== (definitionData.requiresBooking ?? false) ||
    editFormData.isActive !== (definitionData.isActive ?? true)
  ) : false;

  const handleDefinitionClick = (definitionId: string) => {
    setSelectedDefinitionId(definitionId);
    setFlyoutMode('view');
  };

  const handleEdit = () => {
    setFlyoutMode('edit');
  };

  const handleSave = async () => {
    if (!definitionData) return;

    try {
      await updateServiceDefinition({
        id: definitionData.id,
        ...editFormData,
        typicalDuration: editFormData.typicalDuration ? parseInt(editFormData.typicalDuration) : undefined
      });

      await refetch();
      setFlyoutMode('view');

      toast({
        title: "Success",
        description: "Service Definition updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update service definition",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!definitionData) return;
    if (confirm('Are you sure you want to delete this service definition? This action cannot be undone.')) {
      try {
        await deleteServiceDefinition({ id: definitionData.id });
        setSelectedDefinitionId(null);
        await refetch();
        toast({
          title: "Success",
          description: "Service Definition deleted successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: 'Failed to delete service definition: ' + (error as Error).message,
          variant: "destructive",
        });
      }
    }
  };

  // Navigation helpers
  const currentDefinitionIndex = filteredData.findIndex(d => d.id === selectedDefinitionId) ?? -1;
  const hasPrevious = currentDefinitionIndex > 0;
  const hasNext = currentDefinitionIndex >= 0 && currentDefinitionIndex < filteredData.length - 1;

  const handleNavigatePrevious = () => {
    if (hasPrevious && filteredData) {
      const previousDefinition = filteredData[currentDefinitionIndex - 1];
      setSelectedDefinitionId(previousDefinition.id);
    }
  };

  const handleNavigateNext = () => {
    if (hasNext && filteredData) {
      const nextDefinition = filteredData[currentDefinitionIndex + 1];
      setSelectedDefinitionId(nextDefinition.id);
    }
  };

  const hasData = (serviceDefinitions as any[]).length > 0;

  return (
    <>
      <div className="w-full">
        {/* Fixed Header */}
        <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Service Names</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Manage service templates and definitions ({filteredData.length} total)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters(f => !f)}
                  className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                    showFilters
                      ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-transparent dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filter
                </button>
                {isBulkUploadEnabled && (
                  <EnhancedActionButton
                    onClick={() => setShowBulkUpload(true)}
                    icon={Upload}
                    label="Bulk Upload"
                    variant="secondary"
                  />
                )}
                <EnhancedActionButton
                  onClick={() => navigate('/admin/service-definitions/new')}
                  icon={Plus}
                  label="Create Definition"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Spacer to push content below fixed header */}
        <div className="h-20" />

        {/* Table Header with Filters */}
        {hasData && (
          <TableFilterLayout
            showFilters={showFilters}
            columns={[
              { title: 'Name', span: 3 },
              { title: 'Slug', span: 2 },
              { title: 'Service Type', span: 2 },
              { title: 'Description', span: 3 },
              { title: 'Offerings', span: 2 }
            ]}
            filters={[
              {
                span: 3,
                content: (
                  <input
                      type="text"
                      placeholder="Search name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                )
              },
              {
                span: 2,
                content: (
                  <input
                    type="text"
                    placeholder="Filter by slug..."
                    value={slugFilter}
                    onChange={(e) => setSlugFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                )
              },
              {
                span: 2,
                content: (
                  <select
                    value={selectedTypeId}
                    onChange={(e) => setSelectedTypeId(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Types</option>
                    {(serviceTypes as any[]).map((type: any) => (
                      <option key={type.id} value={type.id}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                )
              },
              { span: 3, content: <div className="h-10" /> },
              { span: 2, content: <div className="h-10" /> }
            ]}
          />
        )}

        {/* Content */}
        <div className="w-full">
            {isLoading && <LoadingSpinner />}
            {error && (
              <div className="p-4 text-center text-red-600">
                Error loading service definitions: {error.message}
              </div>
            )}

            {!isLoading && !error && filteredData.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <p className="text-lg font-medium">No service definitions found</p>
                <p className="text-sm mt-2">
                  {searchTerm || slugFilter || selectedTypeId ? "Try adjusting your filters" : "Click 'Create Definition' to add your first service definition"}
                </p>
              </div>
            )}

            {filteredData.length > 0 && (
              <>
                {filteredData.map((def: any) => (
                  <div
                    key={def.id}
                    className="px-4 py-2.5 admin-hoverable transition-colors grid grid-cols-12 gap-4 items-center border-b border-gray-200 dark:border-gray-700 cursor-pointer"
                    onClick={() => handleDefinitionClick(def.id)}
                    title="Click to view details"
                  >
                    <div className="col-span-3 overflow-hidden min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate block">
                        {def.name}
                      </div>
                      <div className="flex gap-2 mt-1">
                        {def.isRecurring && (
                          <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded">
                            Recurring
                          </span>
                        )}
                        {def.requiresBooking && (
                          <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded">
                            Booking
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {def.slug}
                      </span>
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {def.serviceType?.label || '-'}
                      </span>
                    </div>
                    <div className="col-span-3 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                        {def.description || '-'}
                      </span>
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate block">
                        {(def as any)._count?.offerings || 0} locations
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
        </div>

        {/* Flyout Pane */}
        <AdminFlyoutPane
          open={!!selectedDefinitionId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedDefinitionId(null);
              setFlyoutMode('view');
            }
          }}
          title={flyoutMode === 'view' ? 'View Service Definition' : 'Edit Service Definition'}
          description={definitionData?.slug || undefined}
          width="2xl"
          mode={flyoutMode}
          onNavigatePrevious={handleNavigatePrevious}
          onNavigateNext={handleNavigateNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          headerActions={
            flyoutMode === 'view' ? (
              <>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white w-[110px]"
                >
                  Delete
                </Button>
                <Button onClick={handleEdit} size="sm" className="w-[110px]">
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFlyoutMode('view')}
                  className="w-[110px]"
                >
                  {hasChanges ? 'Cancel' : 'Close'}
                </Button>
                <Button
                  onClick={handleSave}
                  size="sm"
                  className="w-[110px]"
                  disabled={!hasChanges}
                >
                  Save
                </Button>
              </>
            )
          }
        >
          {loadingDefinition && !selectedDefinition ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : definitionData ? (
            <>
              <FlyoutGridLayout>
                {/* Basic Information Section */}
                <FlyoutGridSection title="BASIC INFORMATION">
                  <FlyoutGridRow
                    label="Name"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          value={editFormData.name}
                          onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter service name"
                        />
                      ) : (
                        <span>{definitionData.name}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Slug"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          value={editFormData.slug}
                          onChange={(e) => setEditFormData({ ...editFormData, slug: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter URL slug"
                        />
                      ) : (
                        <span className="text-xs text-gray-600">{definitionData.slug}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Service Type"
                    value={
                      flyoutMode === 'edit' ? (
                        <Select
                          value={editFormData.serviceTypeId}
                          onValueChange={(value) => setEditFormData({ ...editFormData, serviceTypeId: value })}
                        >
                          <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1">
                            <SelectValue placeholder="Select a service type" />
                          </SelectTrigger>
                          <SelectContent>
                            {(serviceTypes as any[]).map((type: any) => (
                              <SelectItem key={type.id} value={type.id}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span>{definitionData.serviceType?.label || '-'}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Description"
                    value={
                      flyoutMode === 'edit' ? (
                        <Textarea
                          value={editFormData.description}
                          onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                          className="text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1 min-h-[60px]"
                          placeholder="Enter short description"
                        />
                      ) : (
                        <span>{definitionData.description || 'None'}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Detailed Description"
                    value={
                      flyoutMode === 'edit' ? (
                        <Textarea
                          value={editFormData.detailedDescription}
                          onChange={(e) => setEditFormData({ ...editFormData, detailedDescription: e.target.value })}
                          className="text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1 min-h-[100px]"
                          placeholder="Enter detailed description"
                        />
                      ) : (
                        <span className="whitespace-pre-wrap">{definitionData.detailedDescription || 'None'}</span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Service Characteristics Section */}
                <FlyoutGridSection title="SERVICE CHARACTERISTICS">
                  <FlyoutGridRow
                    label="Typical Duration"
                    value={
                      flyoutMode === 'edit' ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={editFormData.typicalDuration}
                            onChange={(e) => setEditFormData({ ...editFormData, typicalDuration: e.target.value })}
                            className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1 w-20"
                            placeholder="0"
                          />
                          <span className="text-sm text-gray-600">minutes</span>
                        </div>
                      ) : (
                        <span>{definitionData.typicalDuration ? `${definitionData.typicalDuration} minutes` : 'Not specified'}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Is Recurring"
                    value={
                      flyoutMode === 'edit' ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editFormData.isRecurring}
                            onCheckedChange={(checked) => setEditFormData({ ...editFormData, isRecurring: checked })}
                          />
                          <span className="text-sm">{editFormData.isRecurring ? 'Yes' : 'No'}</span>
                        </div>
                      ) : (
                        <span className={definitionData.isRecurring ? 'text-green-600 font-medium' : 'text-gray-600'}>
                          {definitionData.isRecurring ? 'Yes' : 'No'}
                        </span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Requires Booking"
                    value={
                      flyoutMode === 'edit' ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editFormData.requiresBooking}
                            onCheckedChange={(checked) => setEditFormData({ ...editFormData, requiresBooking: checked })}
                          />
                          <span className="text-sm">{editFormData.requiresBooking ? 'Yes' : 'No'}</span>
                        </div>
                      ) : (
                        <span className={definitionData.requiresBooking ? 'text-yellow-600 font-medium' : 'text-gray-600'}>
                          {definitionData.requiresBooking ? 'Yes' : 'No'}
                        </span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Settings Section */}
                <FlyoutGridSection title="SETTINGS">
                  <FlyoutGridRow
                    label="Active Status"
                    value={
                      flyoutMode === 'edit' ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editFormData.isActive}
                            onCheckedChange={(checked) => setEditFormData({ ...editFormData, isActive: checked })}
                          />
                          <span className="text-sm">{editFormData.isActive ? 'Active' : 'Inactive'}</span>
                        </div>
                      ) : (
                        <span className={definitionData.isActive ? 'text-green-600 font-medium' : 'text-gray-600'}>
                          {definitionData.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Statistics Section (View Only) */}
                {flyoutMode === 'view' && (
                  <FlyoutGridSection title="STATISTICS">
                    <FlyoutGridRow
                      label="Offerings"
                      value={<span>{definitionData._count?.offerings || 0} locations</span>}
                    />
                    <FlyoutGridRow
                      label="Linked Topics"
                      value={<span>{definitionData._count?.topicServices || 0}</span>}
                    />
                    <FlyoutGridRow
                      label="Linked Occasions"
                      value={<span>{definitionData._count?.occasionServices || 0}</span>}
                      isLast
                    />
                  </FlyoutGridSection>
                )}
              </FlyoutGridLayout>
            </>
          ) : null}
        </AdminFlyoutPane>
      </div>
      <BulkUploadDialog
        open={showBulkUpload}
        onOpenChange={setShowBulkUpload}
        config={serviceDefinitionConfig}
        onComplete={() => refetch()}
      />
    </>
  );
}



